import { FlakinessReport } from '@flakiness/flakiness-report';
import { Brand, randomUUIDBase62, xxHash, xxHashObject } from '@flakiness/shared/common/utils.js';
import { Ranges } from '../ranges.js';
import { WireTypes } from '../wireTypes.js';

export namespace Stats {
  export const STATS_VERSION = 16;

  export type CommitId = FlakinessReport.CommitId;
  export type CommitIndex = Brand<number, 'Stats.CommitIndex'>;

  export type RunId = Brand<number, 'Stats.RunId'>;
  export type ReportIndex = Brand<number, 'Stats.ReportIndex'>;

  export type TestId = Brand<string, 'Stats.TestId'>;
  export type TestIndex = Brand<number, 'Stats.TestIndex'>;

  export type EnvId = Brand<string, 'Stats.EnvId'>;
  export type EnvIndex = Brand<number, 'Stats.EnvIndex'>;

  export type TimelineIndex = Brand<number, 'Stats.TimelineIndex'>;

  export type ErrorId = Brand<string, 'Stats.ErrorId'>;
  export type ErrorIndex = Brand<number, 'Stats.ErrorIndex'>;

  export type AnnotationId = Brand<string, 'Stats.AnnotationId'>;
  export type AnnotationIndex = Brand<number, 'Stats.AnnotationIndex'>;

  export type AnnotationsProfileId = Brand<string, 'Stats.AnnotationsProfileId'>;
  export type AnnotationsProfileIndex = Brand<number, 'Stats.AnnotationsProfileIndex'>;

  export type RunIndex = Brand<number, 'Stats.RunIndex'>;

  export type TestTreeNode = {
    fileIdx?: number;
    idx: TestIndex;
  };

  export type SuiteTreeNode = {
    // Use abbreviations to save on data.
    c?: Record<string, SuiteTreeNode>;
    t?: TestTreeNode[], 
  };

  export type TestsEpoch = Brand<string, 'Stats.TestsEpoch'>;

  export type JSONTestsAndEnvs = {
    epoch: TestsEpoch,
    envs: JSONEnvironment[],
    files: FlakinessReport.GitFilePath[],
    testsRoot: SuiteTreeNode,
  }

  export type JSONEnvironment = {
    name: string,
    category: string,
    configPath?: FlakinessReport.GitFilePath;
    systemData: {
      osName: string,
      osVersion: string,
      osArch: string,
    },
    // Absent when the report has no runtime, so environments ingested before
    // runtimes were recorded keep their ids.
    runtime?: {
      name: string,
      version: string,
    },
    userSuppliedData?: Record<string, string|boolean|number>,
  }

  export type JSONError = {
    message?: string,
    value?: string,
  }

  export type JSONAnnotation = {
    type: string,
    description?: string,
  }

  export type JSONRunAnnotation = {
    annotationIdx: AnnotationIndex,
    tests: Ranges.CompressedRanges<TestIndex>,
  };

  // For each report, the set of annotations for different tests has a tendency to repeat itself.
  // So we store a set of annotations-for-tests as "profiles".
  export type JSONAnnotationsProfile = JSONRunAnnotation[];

  export type JSONData = {
    // schema version
    version: number,
    epoch: TestsEpoch,

    testLineNumbers: FlakinessReport.Number1Based[],

    errors: JSONError[],

    annotations: JSONAnnotation[],
    annotationProfiles: JSONAnnotationsProfile[],

    commits: JSONCommit[],
  }

  export type JSONCommit = {
    commitId: CommitId,
    changedLineNumbers?: {
      lineDelta: number,
      affectedTests: Ranges.CompressedRanges<TestIndex>,
    }[],
    tags: {
      tag: string,
      tests: Ranges.CompressedRanges<TestIndex>,
    }[],
    runs: JSONRun[];
  }

  export type JSONRun = {
    url: string,
    title?: string,
    runId: RunId,
    startTimestamp: FlakinessReport.UnixTimestampMS,
    duration: FlakinessReport.DurationMS,

    attempts: JSONAttempt[],
  };

  /**
   * This stores all test attempts for a report. Each attempt has an environment
   * and attemptIdx (deafults to 0 for the first attempt).
   * Usually, the same test is retried in the same environment and has the same
   * annotations, but nevertheless we still store environmentIdx for each attempt to keep the data structure simple.
   */
  export type JSONAttempt = {
    environmentIdx: EnvIndex,
    attemptIdx?: number,

    expectedTests?: Ranges.CompressedRanges<TestIndex>,
    unexpectedTests?: Ranges.CompressedRanges<TestIndex>,
    skippedTests?: Ranges.CompressedRanges<TestIndex>,
    flakedTests?: Ranges.CompressedRanges<TestIndex>,

    // Artifacts
    testsWithVideo?: Ranges.CompressedRanges<TestIndex>,
    testsWithTrace?: Ranges.CompressedRanges<TestIndex>,
    testsWithImage?: Ranges.CompressedRanges<TestIndex>,

    errors?: JSONRunError[],
    annotationsProfileIdx: AnnotationsProfileIndex,

    durationBuckets: FlakinessReport.DurationMS[],
    durationBucketTests: Ranges.CompressedRanges<TestIndex>[],
  }

  export type JSONRunError = {
    errorIdx: ErrorIndex,
    tests: Ranges.CompressedRanges<TestIndex>,
  };

  export function createEmptyStats(epoch: TestsEpoch): JSONData {
    return {
      epoch,
      version: STATS_VERSION,
      testLineNumbers: [],
      annotations: [],
      annotationProfiles: [],
      errors: [],
      commits: [],
    };
  }

  export function createEmptyTests(): JSONTestsAndEnvs {
    return {
      epoch: `test-epoch-${randomUUIDBase62()}` as TestsEpoch,
      envs: [],
      files: [],
      testsRoot: {},
    };
  }

  // Keys Playwright auto-injects into config.metadata (inherited by project.metadata).
  // They are not user intent and fragment timelines, so we strip them during ingestion.
  const PLAYWRIGHT_NOISE_METADATA_KEYS = new Set(['actualWorkers', 'ci', 'gitCommit', 'gitDiff']);

  export function flakinessEnvToJSONEnv(report: FlakinessReport.Report, env: FlakinessReport.Environment): JSONEnvironment {
    const isPlaywright = report.category === FlakinessReport.CATEGORY_PLAYWRIGHT;
    return {
      name: env.name,
      category: report.category,
      configPath: report.configPath,
      // Explicitly copy nested object fields: if report.environment gets extended,
      // then we have to explicitly extend the JSONEnvironment type to support it.
      systemData: {
        osArch: env.systemData?.osArch ?? 'unknown',
        osName: env.systemData?.osName ?? 'unknown',
        osVersion: env.systemData?.osVersion ?? 'unknown',
      },
      // The runtime is reported once per report, so every environment gets it.
      ...(report.runtime ? {
        runtime: {
          name: report.runtime.name,
          version: report.runtime.version,
        },
      } : {}),
      // Currently, we only support a limited set of values for the `userSuppliedData`.
      userSuppliedData: Object.fromEntries(Object.entries(env.metadata ?? env.userSuppliedData ?? {}).filter(([key, value]) => {
        if (isPlaywright && PLAYWRIGHT_NOISE_METADATA_KEYS.has(key))
          return false;
        return ['string', 'number', 'boolean'].includes(typeof value);
      })),
    };
  }

  export function jsonEnvToWireEnv(jsonEnv: JSONEnvironment): WireTypes.RunEnvironment {
    return {
      envId: computeEnvId(jsonEnv),
      category: jsonEnv.category,
      name: jsonEnv.name,
      configPath: jsonEnv.configPath,
      systemData: {
        osArch: jsonEnv.systemData.osArch,
        osName: jsonEnv.systemData.osName,
        osVersion: jsonEnv.systemData.osVersion,
      },
      runtime: jsonEnv.runtime ? {
        name: jsonEnv.runtime.name,
        version: jsonEnv.runtime.version,
      } : undefined,
      userSuppliedData: jsonEnv.userSuppliedData,
    };
  }

  export function computeTestId(options: { file?: string, titles: string[] }): TestId {
    return xxHash([
      options.file ?? '', 
      ...options.titles
    ]) as TestId;
  }

  export function flakinessTestToWireTypesTest(test: FlakinessReport.Test, parentSuites: FlakinessReport.Suite[]): WireTypes.Test {
    // For file suites, we consider their file path as their name, if any, and fallback to title.
    const suiteTitles = parentSuites.map(suite => suite.type === 'file' ? suite.location?.file ?? suite.title : suite.title);
    // If the very first suite matches the test's location, then this "file suite" can be omitted:
    // test reporters often report a top-level file suite for the tests inside the file.
    // And they don't always mark these as "files" as well: lots of generated JUnit XML's report all suites as "suites",
    // wihout distinction, so we explicitly avoid checkinf the suite type here.
    // Sometimes, however, top-level suite IS a file suite, and it is different from test's location.
    // This means that some root file imported some helpers to declare tests - and we should keep it.
    if (suiteTitles[0] && suiteTitles[0] === test.location?.file)
      suiteTitles.splice(0, 1);
    const titles = [...suiteTitles, test.title];
    const testId = computeTestId({
      file: test.location?.file,
      titles: titles,
    });
    return {
      filePath: test.location?.file,
      testId,
      titles,
    };
  }

  export function computeEnvId(env: JSONEnvironment): EnvId {
    return xxHashObject(env) as EnvId;
  }

  export function computeErrorId(error: JSONError): ErrorId {
    return xxHashObject(error) as ErrorId;
  }

  export function computeAnnotationId(annotation: JSONAnnotation): AnnotationId {
    return xxHashObject(annotation) as AnnotationId;
  }

  export function computeAnnotationsProfileId(profile: JSONAnnotationsProfile): AnnotationsProfileId {
    profile.sort((a, b) => a.annotationIdx - b.annotationIdx);
    return xxHash(profile.map(xxHashObject)) as AnnotationsProfileId;
  }

  export function computeDataSignature(s: JSONData) {
    const runIds = s.commits.map(commit => commit.runs.map(run => run.runId)).flat();
    runIds.sort();
    return xxHashObject({
      runIds: runIds.map(report => String(report)),
      epoch: s.epoch,
      version: s.version,
    });
  }

  export function computeAttemptOutcome(attempt: FlakinessReport.RunAttempt) {
    const expected = attempt.expectedStatus ?? 'passed';
    const actual = attempt.status ?? 'passed';
    if (actual === 'skipped' || actual === 'interrupted')
      return 'skipped';
    return actual === expected ? 'expected' : 'unexpected';
  }
}
